import { useState, useCallback, useRef } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../../Keycloak/useAuthenticatedFetchWithWorkspace";
import { Customer, CustomerSearchParams } from "../../../types/Customer";

const toList = (result: unknown): Customer[] => {
    if (Array.isArray(result)) return result as Customer[];
    if (result && typeof result === "object") {
        const r = result as Record<string, unknown>;
        if (Array.isArray(r["hydra:member"])) return r["hydra:member"] as Customer[];
        if (Array.isArray(r.list))            return r.list as Customer[];
        if (Array.isArray(r.member))          return r.member as Customer[];
        if (Object.keys(r).length > 0 && typeof r[Object.keys(r)[0]] === "object")
            return Object.values(r) as Customer[];
    }
    return [];
};

// N'inclut dans le body que les champs effectivement renseignés, pour alléger le payload.
const buildBody = (params: CustomerSearchParams): Record<string, unknown> => {
    const body: Record<string, unknown> = {
        identifiers:       params.identifiers?.length ? params.identifiers : ["0"],
        person_attributes: params.personAttributes?.length ? params.personAttributes : ["0"],
    };
    const s = (key: string, val: string | undefined) => { if (val?.trim()) body[key] = val.trim(); };
    s("name",                       params.lastname);
    s("firstname",                  params.firstname);
    s("identifier",                 params.identifier);
    s("web_identifier",             params.webIdentifier);
    s("personId",                   params.personId);
    s("address1",                   params.address1);
    s("address2",                   params.address2);
    s("address3_num",               params.address3Num);
    s("address3Type",               params.address3Type);
    s("address3_name",              params.address3Name);
    s("address4",                   params.address4);
    s("zipcode",                    params.zipcode);
    s("city",                       params.city);
    s("country",                    params.country);
    s("phone",                      params.phone);
    s("email",                      params.email);
    s("cellphone",                  params.cellphone);
    s("birthdate",                  params.birthdate);
    s("transaction_receipt_number", params.transactionReceiptNumber);
    s("transaction_item_reference", params.transactionItemReference);
    s("transaction_amount_min",     params.transactionAmountMin);
    s("transaction_amount_max",     params.transactionAmountMax);
    s("transaction_date",           params.transactionDate);
    s("transaction_hour",           params.transactionHour);
    s("entity_id",                  params.entityId);
    return body;
};

const isEmptyApiResponse = (err: unknown) =>
    err instanceof Error && /HTTP 5\d\d/.test(err.message) && err.message.endsWith(": ");

const CACHE_MAX = 30;

const useSearchPersons = () => {
    const { authFetch } = useAuthenticatedFetchWithWorkspace();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const abortControllerRef = useRef<AbortController | null>(null);
    // Cache in-memory : clé = JSON du body normalisé, valeur = résultats
    const cacheRef = useRef<Map<string, Customer[]>>(new Map());

    const searchPersons = useCallback(async (params: CustomerSearchParams): Promise<void> => {
        const body = buildBody(params);
        const cacheKey = JSON.stringify(body);

        // Cache hit : résultat instantané, pas d'appel réseau
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
            setCustomers(cached);
            setError("");
            return;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setError("");

        try {
            const result = await authFetch("/getPerson", {
                method: "POST",
                body: JSON.stringify(body),
                signal: controller.signal,
            });

            const list = toList(result);
            // Évite une fuite mémoire si le cache grossit trop
            if (cacheRef.current.size >= CACHE_MAX) cacheRef.current.clear();
            cacheRef.current.set(cacheKey, list);
            setCustomers(list);
        } catch (err) {
            if (err instanceof Error && err.name === "AbortError") return;
            setCustomers([]);
            if (!isEmptyApiResponse(err)) {
                setError(err instanceof Error ? err.message : "Une erreur est survenue");
            }
        } finally {
            if (abortControllerRef.current === controller) {
                setIsLoading(false);
            }
        }
    }, [authFetch]);

    const resetSearch = useCallback(() => {
        cacheRef.current.clear();
        setCustomers([]);
        setError("");
    }, []);

    return { customers, isLoading, error, searchPersons, resetSearch };
};

export default useSearchPersons;
